/**
 * Bulk email service for sending salary specifications to multiple drivers
 *
 * Frontend implementation that loops through drivers and calls the existing
 * sendSalaryEmail Cloud Function for each driver sequentially, with a delay between each send.
 * This is not true batching or rate limiting, but sequential processing with delays.
 */

import { functions, httpsCallable } from '@/lib/firebase';
import type { DriverMaster } from '@/types/driverMaster';
import type { SalaryReportRow } from '@/components/reports/reportConfig';

/**
 * Request type for individual email send
 */
interface SendSalaryEmailRequest {
  driverEmail: string;
  driverName: string;
  periodName: string;
  driverId?: string;
}

/**
 * Response type for individual email send
 */
interface SendSalaryEmailResponse {
  success: boolean;
  messageId?: string;
}

/**
 * Error details for a failed driver email
 */
export interface DriverEmailError {
  driverName: string;
  driverEmail: string;
  error: string;
}

/**
 * Result of bulk email sending
 */
export interface BulkEmailResult {
  sent: number;
  failed: DriverEmailError[];
  skipped: number;
}

/**
 * Delay between emails in milliseconds
 * 1000ms = 1 email/second, well within Resend's 2 req/sec rate limit
 */
const EMAIL_DELAY_MS = 1000;

/**
 * Validate email format using basic regex
 * @param email - Email address to validate
 * @returns true if email format is valid
 */
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Driver with valid email
 */
interface DriverWithEmail {
  id: string;
  name: string;
  email: string;
}

/**
 * Result of extracting drivers from report data
 */
interface ExtractedDrivers {
  withEmail: DriverWithEmail[];
  uniqueDriverCount: number;
}

/**
 * Extract drivers with valid email addresses from report data
 * Shared logic between sendBulkSalaryEmails and getBulkEmailPreview
 *
 * @returns Object with drivers that have valid emails and total unique driver count
 */
function extractDriversFromReport(
  reportData: SalaryReportRow[],
  driverMasters: DriverMaster[]
): ExtractedDrivers {
  const driverLookup = new Map(driverMasters.map((d) => [d.id, d]));
  const seenDriverIds = new Set<string>();
  const driversWithEmail: DriverWithEmail[] = [];

  for (const row of reportData) {
    if (seenDriverIds.has(row.driverId)) continue;
    seenDriverIds.add(row.driverId);

    const driver = driverLookup.get(row.driverId);
    const email = driver?.email?.trim();

    if (driver && email && isValidEmail(email)) {
      driversWithEmail.push({
        id: row.driverId,
        name: driver.primaryName,
        email,
      });
    }
  }

  return {
    withEmail: driversWithEmail,
    uniqueDriverCount: seenDriverIds.size,
  };
}

/**
 * Send salary specification emails to multiple drivers
 *
 * Sends emails sequentially with rate limiting (1 email/second) to respect
 * Resend's API limits. Uses the existing sendSalaryEmail Cloud Function.
 *
 * @param reportData - Salary report rows (may contain multiple rows per driver; duplicates are automatically deduplicated)
 * @param driverMasters - Driver master data (includes email addresses)
 * @param periodName - Period name for email content (e.g., "2025-01")
 * @param onProgress - Optional callback for progress updates (current, total)
 * @returns Result with statistics about sent/failed/skipped emails
 *
 * @example
 * ```typescript
 * const result = await sendBulkSalaryEmails(
 *   reportData,
 *   driverMasters,
 *   '2025-01',
 *   (current, total) => console.log(`Progress: ${current}/${total}`)
 * );
 * console.log(`Sent: ${result.sent}, Failed: ${result.failed.length}`);
 * ```
 */
export async function sendBulkSalaryEmails(
  reportData: SalaryReportRow[],
  driverMasters: DriverMaster[],
  periodName: string,
  onProgress?: (current: number, total: number) => void
): Promise<BulkEmailResult> {
  // Use the pre-configured functions instance from firebase.ts (includes emulator setup)
  const sendSalaryEmail = httpsCallable<
    SendSalaryEmailRequest,
    SendSalaryEmailResponse
  >(functions, 'sendSalaryEmail');

  // Get drivers with valid emails
  const { withEmail: driversToEmail, uniqueDriverCount } =
    extractDriversFromReport(reportData, driverMasters);

  // Track results
  let sent = 0;
  const failed: DriverEmailError[] = [];
  // skipped = unique drivers without valid email (not counting duplicates)
  const skipped = uniqueDriverCount - driversToEmail.length;

  // Send emails sequentially with rate limiting
  for (let i = 0; i < driversToEmail.length; i++) {
    const driver = driversToEmail[i];

    try {
      await sendSalaryEmail({
        driverEmail: driver.email,
        driverName: driver.name,
        periodName,
        driverId: driver.id,
      });
      sent++;
    } catch (error: unknown) {
      console.error('Email send failed for driver:', {
        driverName: driver.name,
        driverEmail: driver.email,
        error: error instanceof Error ? error : { unknown: error },
      });

      const errorMessage =
        error instanceof Error ? error.message : 'Okänt fel';

      failed.push({
        driverName: driver.name,
        driverEmail: driver.email,
        error: errorMessage,
      });
    }

    // Report progress after each email
    onProgress?.(sent + failed.length, driversToEmail.length);

    // Wait before sending next email (skip delay after last email)
    if (i < driversToEmail.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, EMAIL_DELAY_MS));
    }
  }

  return {
    sent,
    failed,
    skipped,
  };
}

/**
 * Get preview information before sending bulk emails
 *
 * @param reportData - Salary report rows
 * @param driverMasters - Driver master data
 * @returns Preview with counts (unique drivers, not report rows)
 */
export function getBulkEmailPreview(
  reportData: SalaryReportRow[],
  driverMasters: DriverMaster[]
): {
  totalDrivers: number;
  withEmail: number;
  withoutEmail: number;
} {
  const { withEmail, uniqueDriverCount } = extractDriversFromReport(
    reportData,
    driverMasters
  );

  return {
    totalDrivers: uniqueDriverCount,
    withEmail: withEmail.length,
    withoutEmail: uniqueDriverCount - withEmail.length,
  };
}
